import { useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { setCategories, type NoteType, type Category } from './api';
import { draftKey, readDraft, removeDraft, writeDraft } from './drafts';
type TypeDraft = { codes: string[]; revision: number };
function isDraft(value: unknown): value is TypeDraft {
  return (
    typeof value === 'object' &&
    value !== null &&
    'codes' in value &&
    Array.isArray(value.codes) &&
    value.codes.every((c) => typeof c === 'string') &&
    'revision' in value &&
    typeof value.revision === 'number'
  );
}
export function useTypeEditor(
  setting: NoteType,
  refresh: () => void,
  categories: readonly Category[],
) {
  const { t } = useTranslation('expenses');
  const key = draftKey(`type:${setting.noteType}`);
  const [initial] = useState(() => {
    const saved = readDraft(key);
    return isDraft(saved) ? saved : { codes: setting.categoryCodes, revision: setting.revision };
  });
  const [codes, setCodes] = useState(() => normalisedCodes(initial.codes, categories));
  const [revision, setRevision] = useState(initial.revision);
  const [error, setError] = useState(
    initial.revision === setting.revision ? '' : 'CONFIGURATION_CHANGED',
  );
  const [busy, setBusy] = useState(false);
  const changed = [...codes].sort().join() !== [...setting.categoryCodes].sort().join();
  function toggle(code: string) {
    if (categories.find((category) => category.code === code)?.required) return;
    const next = codes.includes(code) ? codes.filter((c) => c !== code) : [...codes, code];
    setCodes(next);
    writeDraft(key, { codes: next, revision });
  }
  function useLatest() {
    setRevision(setting.revision);
    writeDraft(key, { codes, revision: setting.revision });
    setError('');
  }
  async function save() {
    if (busy || codes.length === 0) return;
    setBusy(true);
    setError('');
    const outcome = await setCategories(setting.noteType, revision, codes);
    setBusy(false);
    if (outcome.ok) {
      removeDraft(key);
      toast.success(t('configuration.saved'));
      refresh();
    } else setError(outcome.code);
  }
  return { codes, revision, error, busy, changed, toggle, useLatest, save };
}

function normalisedCodes(codes: readonly string[], categories: readonly Category[]) {
  const allowed = new Set(categories.map((category) => category.code));
  return [
    ...new Set([
      ...codes.filter((code) => allowed.has(code)),
      ...categories.filter((category) => category.required).map((category) => category.code),
    ]),
  ].sort();
}
